import { Graphics, type Renderer, type Texture } from 'pixi.js';
import type { EnemyDef } from '@playnets/sim';

/**
 * Enemies are silhouettes only — flat fills, no interior detail (§10).
 * Readability at twenty entities beats prettiness at one, and inside a
 * 420-unit circle that is not a stylistic preference, it's the difference
 * between a readable board and soup (risk #6).
 *
 * Each shape is drawn pointing "up" (toward negative y) and the sprite is
 * rotated to face the planet at draw time.
 */
function silhouette(def: EnemyDef, outline: number): Graphics {
  const g = new Graphics();
  const s = def.size;
  const c = def.colour;

  switch (def.behaviour) {
    case 'fan': // Drone — a dart. Arrives in fans of eight to twelve.
      g.poly([0, -s, s * 0.72, s * 0.55, 0, s * 0.2, -s * 0.72, s * 0.55]).fill({ color: c });
      break;

    case 'splitter': // Asteroid — lumpy heptagon, obviously breakable.
      g.poly([
        0, -s, s * 0.72, -s * 0.62, s, s * 0.1, s * 0.55, s * 0.85,
        -s * 0.5, s * 0.9, -s, s * 0.05, -s * 0.7, -s * 0.6,
      ]).fill({ color: c });
      break;

    case 'straight': // Shard — half an asteroid, and it looks it.
      g.poly([0, -s, s * 0.8, s * 0.3, 0, s * 0.75, -s * 0.62, s * 0.2]).fill({ color: c });
      break;

    case 'shielded': // Cruiser — the shield reads as a heavy leading edge.
      g.poly([0, s, s * 0.6, -s * 0.2, s * 0.5, -s * 0.75, -s * 0.5, -s * 0.75, -s * 0.6, -s * 0.2])
        .fill({ color: c });
      g.poly([-s * 0.95, -s * 0.72, s * 0.95, -s * 0.72, s * 0.7, -s * 1.05, -s * 0.7, -s * 1.05])
        .fill({ color: c, alpha: 0.55 });
      break;

    case 'burrow': // Burrower — a wedge, nose-down, built to go under.
      g.poly([0, -s * 1.15, s * 0.5, 0, s * 0.3, s * 0.8, -s * 0.3, s * 0.8, -s * 0.5, 0])
        .fill({ color: c });
      break;

    case 'carrier': // Carrier — wide hull with visible bays.
      g.poly([0, -s * 0.7, s, -s * 0.2, s * 0.8, s * 0.7, -s * 0.8, s * 0.7, -s, -s * 0.2])
        .fill({ color: c });
      g.rect(-s * 0.55, s * 0.2, s * 0.3, s * 0.35).fill({ color: c, alpha: 0.45 });
      g.rect(s * 0.25, s * 0.2, s * 0.3, s * 0.35).fill({ color: c, alpha: 0.45 });
      break;

    case 'cloak': // Wraith — a thin diamond that barely occupies space.
      g.poly([0, -s, s * 0.45, 0, 0, s, -s * 0.45, 0]).fill({ color: c });
      break;

    case 'feint': // Feint — swept wings. It looks like it can change its mind.
      g.poly([0, -s, s * 0.95, s * 0.35, s * 0.2, s * 0.15, 0, s * 0.7, -s * 0.2, s * 0.15, -s * 0.95, s * 0.35])
        .fill({ color: c });
      break;

    case 'boss': // World Eater — a maw. Nothing subtle about it.
      {
        const spikes = 14;
        const pts: number[] = [];
        for (let i = 0; i < spikes * 2; i++) {
          const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
          const r = i % 2 === 0 ? s : s * 0.66;
          pts.push(Math.cos(a) * r, Math.sin(a) * r);
        }
        g.poly(pts).fill({ color: c });
        g.circle(0, 0, s * 0.34).fill({ color: 0x1b1a2e });
      }
      break;
  }

  // A single confident outline. Six region grounds means no fill colour is
  // safe on its own, and a hard edge is the house style anyway.
  g.stroke({ width: 2.5, color: outline, alpha: 0.55 });
  return g;
}

export function buildEnemyTextures(
  renderer: Renderer,
  defs: Iterable<EnemyDef>,
  outline: number,
): Map<string, Texture> {
  const out = new Map<string, Texture>();
  for (const def of defs) {
    const g = silhouette(def, outline);
    out.set(def.id, renderer.generateTexture({ target: g, resolution: 2 }));
    g.destroy();
  }
  return out;
}

/** Small flat blobs for projectiles. `hot` orange is player output only. */
export function buildProjectileTextures(
  renderer: Renderer,
  hot: number,
): Map<string, Texture> {
  const out = new Map<string, Texture>();

  const bolt = new Graphics().poly([0, -7, 2.6, 4, 0, 6, -2.6, 4]).fill({ color: hot });
  out.set('bolt', renderer.generateTexture({ target: bolt, resolution: 2 }));
  bolt.destroy();

  const flak = new Graphics().circle(0, 0, 4).fill({ color: hot });
  out.set('flak', renderer.generateTexture({ target: flak, resolution: 2 }));
  flak.destroy();

  const drone = new Graphics().rect(-1.6, -5, 3.2, 10).fill({ color: 0xffd166 });
  out.set('drone', renderer.generateTexture({ target: drone, resolution: 2 }));
  drone.destroy();

  const beam = new Graphics().rect(-1, -6, 2, 12).fill({ color: hot });
  out.set('beam', renderer.generateTexture({ target: beam, resolution: 2 }));
  beam.destroy();

  return out;
}

/**
 * A blob of juice. Rounder at the bottom than the top, one offset highlight,
 * heavy rim — it has to read as "pick me up" at 20px against six different
 * region grounds, so it's the one thing besides an enemy drawn with an outline.
 */
export function buildJuiceTexture(renderer: Renderer, juice: number, paper: number): Texture {
  const g = new Graphics();
  const blob = (r: number, colour: number, alpha = 1) => {
    g.moveTo(0, -r * 1.15);
    g.bezierCurveTo(r * 0.95, -r * 0.5, r, r * 0.45, 0, r);
    g.bezierCurveTo(-r, r * 0.45, -r * 0.95, -r * 0.5, 0, -r * 1.15);
    g.fill({ color: colour, alpha });
  };
  blob(14, paper, 0.92);
  blob(11.5, juice);
  g.ellipse(-3.4, -2.4, 3.2, 4).fill({ color: paper, alpha: 0.6 });
  const tex = renderer.generateTexture({ target: g, resolution: 3 });
  g.destroy();
  return tex;
}
